import { useOs } from '@mantine/hooks';
import { ask } from '@tauri-apps/plugin-dialog';
import { relaunch } from '@tauri-apps/plugin-process';
import { check } from '@tauri-apps/plugin-updater';
import { useGetDeviceGroups } from 'gen/client';
import { useEffect, useRef } from 'react';
import useSWR from 'swr';

import { autoScan } from '@ui/Utils/device/autoScan';
import { useIsNewDevice, useRenkiConfig } from '@ui/Utils/device/installationMetadata';
import { DeviceInfo, getDeviceInfo } from '@ui/Utils/device/renkiInvoke';
import { configLoadError, customErrorToast } from '@ui/Utils/toasts';

import DeviceDiscoveryManager from '../DeviceDiscovery/DeviceDiscoveryManager';

export function TauriContainer() {
    const configErrorShownRef = useRef(false);
    const { data: deviceInfo } = useSWR<DeviceInfo>('_/deviceInfo', {
        dedupingInterval: 10_000,
        fetcher: async () => {
            const info = await getDeviceInfo();
            return info;
        },
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
    const os = useOs();
    const { config, loading } = useRenkiConfig();
    const { isNewDevice } = useIsNewDevice();
    const { data: deviceGroups } = useGetDeviceGroups();

    const deviceGroup = deviceGroups?.find((group) => group.installation_id === config?.installationId);

    // Show error toast if config has finished loading but is still undefined (only once per session)
    useEffect(() => {
        if (!loading && !config && !configErrorShownRef.current) {
            configErrorShownRef.current = true;
            customErrorToast(configLoadError);
        }
    }, [loading, config]);

    useEffect(() => {
        (async () => {
            switch (os) {
                case 'windows':
                case 'macos':
                case 'linux': {
                    const update = await check();
                    if (update !== null) {
                        const yes = await ask('Update is available and will be applied when you restart the app.', {
                            cancelLabel: 'Later',
                            kind: 'info',
                            okLabel: 'Restart Now',
                            title: 'Update Available',
                        });
                        if (yes) {
                            await update.downloadAndInstall();
                            await relaunch();
                        }
                    }
                    break;
                }
                default:
                    break;
            }
        })().catch(console.error);
    }, [os]);

    useEffect(() => {
        if (deviceGroup && config?.installationId) {
            autoScan(true, config.installationId).catch(console.error);
        }
    }, [deviceGroup, config?.installationId]);

    if (!config || !deviceInfo) {
        return null;
    }

    return <DeviceDiscoveryManager autoPrompt installationID={config.installationId} isNewDevice={isNewDevice} />;
}
